"use client";

import { useCallback, useMemo } from "react";
import { useBulkMoveAssets } from "@zapier/client";
import { getFolderUrl, useNotification } from "@zapier/utils";
import { Alert, Link, Spinner, Text } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import { MoveModalInner } from "./core/MoveModalInner";
import type { OnMoveOptions } from "./core/types";
import styles from "./core/MoveModal.module.css";

const FOLDER_PREFIX = "folder:";

interface PermissionResult {
  movableIds: string[];
  nonMovableCount: number;
  isLoading: boolean;
  hasChecked: boolean;
}

interface BulkMoveModalProps {
  selectedAssetIds: string[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSettled?: () => void;
  folderId?: string;
  permissionResult: PermissionResult;
}

export function BulkMoveModal({
  selectedAssetIds,
  isOpen,
  setIsOpen,
  onSettled,
  folderId,
  permissionResult,
}: BulkMoveModalProps) {
  const t = useTranslations("move-asset-actions");
  const { createNotification } = useNotification();
  const { bulkMoveAssets } = useBulkMoveAssets({ folderId });

  const {
    movableIds,
    nonMovableCount,
    isLoading: isPermLoading,
    hasChecked,
  } = permissionResult;

  const noneMovable =
    hasChecked && movableIds.length === 0 && selectedAssetIds.length > 0;

  // Extract raw folder IDs from the selection so we can block users from
  // picking one of the selected folders as the destination (cycle prevention).
  // We also reuse these source folder IDs to evaluate source-folder visibility
  // rules for hiding private folders.
  const disabledFolderIds = useMemo(
    () =>
      movableIds
        .filter((id) => id.startsWith(FOLDER_PREFIX))
        .map((id) => id.slice(FOLDER_PREFIX.length)),
    [movableIds],
  );

  const headerOverride = useMemo(() => {
    if (noneMovable) return t("bulk-move.header-no-permission");
    const count =
      !hasChecked || isPermLoading
        ? selectedAssetIds.length
        : movableIds.length;
    return t("bulk-move.header-move-count", { count });
  }, [
    hasChecked,
    isPermLoading,
    movableIds.length,
    noneMovable,
    selectedAssetIds.length,
    t,
  ]);

  const bodyAlert = useMemo(() => {
    if (isPermLoading) {
      return (
        <div className={styles.bulkSpinner}>
          <Spinner />
        </div>
      );
    }
    if (nonMovableCount === 0) return;
    const message = noneMovable
      ? t("bulk-move.alert-no-permission")
      : t("bulk-move.alert-partial-permission", {
          movableCount: movableIds.length,
          totalCount: selectedAssetIds.length,
        });
    return (
      <div className={styles.bulkAlert}>
        <Alert variant="default" status="warning">
          {message}
        </Alert>
      </div>
    );
  }, [
    isPermLoading,
    movableIds.length,
    noneMovable,
    nonMovableCount,
    selectedAssetIds.length,
    t,
  ]);

  const handleMove = useCallback(
    async ({ selectedFolderId, selectedFolderName }: OnMoveOptions) => {
      if (!selectedFolderId || movableIds.length === 0) return;

      setIsOpen(false);

      try {
        const result = await bulkMoveAssets(movableIds, selectedFolderId);

        if (result.failedCount === 0) {
          createNotification({
            message: (
              <Text>
                {t.rich("notifications.move-success", {
                  assetTitle: `${result.movedCount} items`,
                  isBulk: "true",
                  selectedFolderName,
                  folderLink: (chunks) => (
                    <Link href={getFolderUrl(selectedFolderId)}>{chunks}</Link>
                  ),
                })}
              </Text>
            ),
            type: "success",
          });
        } else {
          createNotification({
            message: t("bulk-move.partial-success", {
              movedCount: result.movedCount,
              totalCount: result.totalRequested,
            }),
            type: "warning",
          });
        }
      } catch {
        createNotification({
          title: t("bulk-move.error-title"),
          message: t("bulk-move.error-message"),
          type: "error",
        });
      }

      onSettled?.();
    },
    [bulkMoveAssets, createNotification, movableIds, onSettled, setIsOpen, t],
  );

  if (!isOpen) return;

  return (
    <MoveModalInner
      assetIds={movableIds}
      sourceFolderIds={disabledFolderIds}
      assetParentId={folderId}
      bodyAlert={bodyAlert}
      disabledFolderIds={disabledFolderIds}
      forceSubmitDisabled={noneMovable || isPermLoading}
      headerOverride={headerOverride}
      submitDisabledTooltip={
        noneMovable ? t("bulk-move.header-no-permission") : undefined
      }
      onMove={handleMove}
      setIsOpen={setIsOpen}
    />
  );
}
